import { css } from "@emotion/css";
import { type CalloutType, Callout, ExternalLink } from "@octopusdeploy/design-system-components";
import { fontWeight, space } from "@octopusdeploy/design-system-tokens";
import { LicenseMessageDisposition } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useEffect, useState } from "react";
import { useAnalyticLicenseUsageDispatch } from "~/analytics/Analytics";
import type { LicenseMessageUrgency } from "~/components/Navbar/LicenseCountdownBanner";
import { GetLicenseExpirationUrgency } from "~/components/Navbar/LicenseCountdownBanner";
import EmailLink from "~/primitiveComponents/navigation/EmailLink";
import DateFormatter from "~/utils/DateFormatter";
interface LicenseCalloutProps {
    serialNumber: string;
    effectiveExpiryDate: string;
    daysToEffectiveExpiryDate: number;
    licenseDisposition: LicenseMessageDisposition | undefined;
}
export function LicenseCallout({ serialNumber, effectiveExpiryDate, daysToEffectiveExpiryDate, licenseDisposition }: LicenseCalloutProps) {
    const [licenseExpirationUrgency, setLicenseExpirationUrgency] = useState<LicenseMessageUrgency>("info");
    const dispatchAction = useAnalyticLicenseUsageDispatch();
    useEffect(() => {
        const urgency = GetLicenseExpirationUrgency(licenseDisposition);
        setLicenseExpirationUrgency(urgency);
    }, [daysToEffectiveExpiryDate, licenseDisposition]);
    const supportEmail = <EmailLink emailAddress="support@octopus.com"/>;
    const contactUs = (<>
            <br /> If you’re having trouble using Octopus or have a question, please email our support team at {supportEmail}. We’re here to help.
        </>);
    const buyNowClicked = () => {
        dispatchAction("Click Buy Now in Control Center", { urgency: licenseExpirationUrgency, resource: "License" });
    };
    const getCalloutData = (): {
        calloutTitle: string;
        calloutType: CalloutType;
        calloutContent: JSX.Element;
    } => {
        let calloutTitle = "We hope you\u2019re enjoying Octopus Deploy";
        let calloutType: CalloutType = "information";
        let calloutContent = (<>
                To keep using Octopus after your trial ends on <b>{DateFormatter.dateToCustomFormat(effectiveExpiryDate, "MMMM DD")}</b>, you will need to purchase a license via Control Center. {contactUs}
            </>);
        if (licenseDisposition === LicenseMessageDisposition.Notice) {
            calloutTitle = "Your trial expires soon";
            calloutType = "warning";
            calloutContent = (<>
                    We hope you enjoyed Octopus Deploy. To keep using Octopus after your trial ends on <b>{DateFormatter.dateToCustomFormat(effectiveExpiryDate, "MMMM DD")}</b>, you will need to purchase a license via Control Center. {contactUs}
                </>);
        }
        else if (licenseDisposition === LicenseMessageDisposition.Warning && daysToEffectiveExpiryDate === 0) {
            calloutTitle = "Your trial ends today";
            calloutType = "danger";
            calloutContent = <>We hope you enjoyed Octopus Deploy. If you’d like to continue using Octopus, you will need to purchase a license via Control Center. {contactUs}</>;
        }
        else if ((licenseDisposition === LicenseMessageDisposition.Warning || licenseDisposition === LicenseMessageDisposition.Error) && daysToEffectiveExpiryDate < 0) {
            calloutTitle = "Your cloud trial has expired";
            calloutType = "danger";
            calloutContent = <>We hope you enjoyed Octopus Deploy. If you’d like to continue using Octopus, you will need to purchase a license via Control Center. Otherwise, you will lose access to this Cloud instance soon. {contactUs}</>;
        }
        return { calloutTitle, calloutType, calloutContent };
    };
    const { calloutTitle, calloutType, calloutContent } = getCalloutData();
    return (<Callout title={calloutTitle} type={calloutType}>
            <div>{calloutContent}</div>
            <div className={licenseCalloutStyles.actions}>
                <ExternalLink label={"Buy Now in Control Center"} href={`plan-upgrade?serial=${serialNumber}`} onClick={buyNowClicked}></ExternalLink>
            </div>
        </Callout>);
}
const licenseCalloutStyles = {
    actions: css({
        marginTop: space[4],
        "& a": {
            fontWeight: fontWeight["700"],
        },
    }),
};
